import React, { useState, useRef, useEffect } from 'react';
import { Box, InputBase, IconButton, Popper, Paper, List, ListItem, Fade } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

// Types for feature index
export interface FeatureIndexItem {
  id: string;
  name: string;
  layer: string;
  geometry: any;
  properties: Record<string, any>;
  bounds?: [[number, number], [number, number]]; // [SW, NE] for fitBounds
}

interface FeatureSearchProps {
  featureIndex: FeatureIndexItem[];
  onSelect: (feature: FeatureIndexItem) => void;
  headerStyle?: React.CSSProperties;
  alwaysOpen?: boolean;
}

const FeatureSearch: React.FC<FeatureSearchProps> = ({ featureIndex, onSelect, headerStyle, alwaysOpen = false }) => {
  const [open, setOpen] = useState(alwaysOpen);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FeatureIndexItem[]>([]);
  const [highlighted, setHighlighted] = useState(0);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) setQuery('');
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }
    const q = query.toLowerCase();
    setResults(
      featureIndex.filter(f =>
        (f.name && f.name.toLowerCase().includes(q)) ||
        (f.id && f.id.toLowerCase().includes(q)) ||
        Object.values(f.properties || {}).some(v =>
          typeof v === 'string' && v.toLowerCase().includes(q)
        )
      ).slice(0, 10)
    );
    setHighlighted(0);
  }, [query, featureIndex]);

  const handleSelect = (feature: FeatureIndexItem) => {
    if (!alwaysOpen) setOpen(false);
    setQuery('');
    setResults([]);
    onSelect(feature);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      setHighlighted(h => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      setHighlighted(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && results[highlighted]) {
      handleSelect(results[highlighted]);
    } else if (e.key === 'Escape') {
      if (!alwaysOpen) setOpen(false);
    }
  };

  return (
    <Box ref={anchorRef} sx={{ display: 'flex', alignItems: 'center', ...headerStyle }}>
      {!open ? (
        <IconButton size="small" sx={{ color: '#fff' }} onClick={() => setOpen(true)} title="Rechercher une entité">
          <SearchIcon />
        </IconButton>
      ) : (
        <>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            background: alwaysOpen ? 'transparent' : 'rgba(30,30,40,0.92)',
            borderRadius: 2,
            px: 0.5,
            py: 0,
            boxShadow: alwaysOpen ? 0 : 2,
            minWidth: alwaysOpen ? '100%' : 140,
            maxWidth: alwaysOpen ? '100%' : 140,
            height: 32,
            width: alwaysOpen ? '100%' : 'auto'
          }}>
            <InputBase
              inputRef={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Rechercher..."
              sx={{ color: '#fff', fontSize: alwaysOpen ? 16 : 13, flex: 1, ml: 0.5, height: 26, lineHeight: 1, p: 0 }}
              autoFocus
            />
            {!alwaysOpen && (
              <IconButton size="small" sx={{ color: '#fff', ml: 0.5 }} onClick={() => setOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          {results.length > 0 && (
            <Popper open={results.length > 0} anchorEl={anchorRef.current} placement="bottom-start" transition
              style={{ zIndex: 2000, width: anchorRef.current ? anchorRef.current.offsetWidth : 260 }}
            >
              {({ TransitionProps }) => {
                const width = anchorRef.current ? anchorRef.current.offsetWidth : 260;
                return (
                  <Fade {...TransitionProps} timeout={150}>
                    <Paper sx={{ mt: 1, width: width, maxHeight: 330, overflowY: 'auto', borderRadius: 2, bgcolor: 'rgba(30,30,40,0.97)', color: '#f3f6fa', boxShadow: 4, p: 0 }}>
                      <Box sx={{ px: 1, pt: 1, pb: 0.2, fontSize: '10px', color: '#90caf9', fontWeight: 600 }}>
                        {results.length} résultat{results.length !== 1 ? 's' : ''} trouvé{results.length !== 1 ? 's' : ''}
                      </Box>
                      <List dense sx={{ p: 0 }}>
                        {results.map((f, i) => {
                          // Get first 4 property entries (label, value)
                          const propPairs = Object.entries(f.properties || {}).slice(0, 4);
                          // SVG polygon preview logic
                          let svg = null;
                          if (f.geometry && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon')) {
                            // Flatten coordinates to one polygon for preview
                            let coords = f.geometry.type === 'Polygon'
                              ? f.geometry.coordinates[0]
                              : f.geometry.coordinates[0][0];
                            // Project to fit in 28x28 box
                            const xs = coords.map((pt: [number, number]) => pt[0]);
                            const ys = coords.map((pt: [number, number]) => pt[1]);
                            const minX = Math.min(...xs), maxX = Math.max(...xs);
                            const minY = Math.min(...ys), maxY = Math.max(...ys);
                            const w = maxX - minX || 1, h = maxY - minY || 1;
                            const scale = 22 / Math.max(w, h);
                            const pad = 3;
                            const points = coords.map((pt: [number, number]) => [
                              ((pt[0] - minX) * scale + pad).toFixed(1),
                              (22 - (pt[1] - minY) * scale + pad).toFixed(1)
                            ].join(",")).join(" ");
                            svg = (
                              <svg width={28} height={28} viewBox="0 0 28 28" style={{ display: 'block', marginRight: 7 }}>
                                <polygon points={points} fill="#b0b8c1" stroke="#1976d2" strokeWidth={1.2} />
                              </svg>
                            );
                          }
                          return (
                            <ListItem
                              key={f.id + f.layer}
                              button
                              selected={i === highlighted}
                              onClick={() => handleSelect(f)}
                              sx={{
                                '&.Mui-selected': { bgcolor: 'rgba(25,118,210,0.12)' },
                                borderBottom: '1px solid #23243a',
                                px: 1.2,
                                bgcolor: 'rgba(30,30,40,0.97)',
                                color: '#f3f6fa',
                                minHeight: 32, py: 0.2, gap: 1
                              }}
                            >
                              {svg && <Box sx={{ flexShrink: 0 }}>{svg}</Box>}
                              <Box sx={{ flex: 1, minWidth: 0 }}>

                                <Box sx={{ fontSize: '10px', color: '#f3f6fa', fontWeight: 600, mb: 0.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name || f.id}</Box>
                                {propPairs.map(([k, v], idx) => (
                                  <Box key={k} sx={{ fontSize: '9.5px', color: '#b0b8c1', fontWeight: 400, lineHeight: 1.15, display: 'flex', gap: 0.5 }}>
                                    <span style={{ color: '#90caf9', minWidth: 40 }}>{k}:</span> <span style={{ color: '#f3f6fa', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(v)}</span>
                                  </Box>
                                ))}
                              </Box>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Paper>
                  </Fade>
                );
              }}
            </Popper>
          )}
        </>
      )}
    </Box>
  );
};

export default FeatureSearch;
